import React from 'react';
import { Icon } from '../../components/icons';
import { BLOCK_LABEL, MINUTE_CHOICES, PASS_PERCENT } from '../../model';
import type { TestBlock } from '../../model';
import type { WizardDraft } from '../../wizardStore';
import { minutesLabel } from '../../words';

const BLOCKS: TestBlock[] = ['products', 'vehicles'];

interface Props {
  draft: WizardDraft;
  subgroups: string[];
  moreOpen: boolean;
  onToggleMore: () => void;
  onChange: (patch: Partial<WizardDraft>) => void;
  onNext: () => void;
}

/** Шаг 1: название, время и «Дополнительно» (макет v6; раздел вынесен в «Дополнительно», п. 14 спецификации). */
export const StepName: React.FC<Props> = ({ draft, subgroups, moreOpen, onToggleMore, onChange, onNext }) => {
  const hasTitle = draft.title.trim() !== '';
  return (
    <>
      <div className="tw-card tw-stack-sm">
        <h2>Как назвать тест?</h2>
        <p className="tw-help">Название увидят сотрудники. Пишите так, чтобы было понятно без пояснений.</p>
        <div>
          <label className="tw-label" htmlFor="tw-w-title">Название теста</label>
          <input
            data-guide="wizard-name" id="tw-w-title"
            className="tw-field"
            type="text"
            maxLength={255}
            placeholder="Например: Тормозная система для новичков"
            value={draft.title}
            onChange={(event) => onChange({ title: event.target.value })}
          />
          <p className="tw-help" style={{ marginTop: 8 }}>Название можно будет поменять потом.</p>
        </div>
        <div>
          <div className="tw-label">Сколько минут дать на тест?</div>
          <div className="tw-row" data-guide="wizard-time" role="group" aria-label="Время на тест">
            {MINUTE_CHOICES.map((value) => (
              <button
                key={value}
                type="button"
                className="tw-chip tw-chip-wide"
                aria-pressed={draft.minutes === value}
                onClick={() => onChange({ minutes: value })}
              >
                {`${value} мин`}
              </button>
            ))}
          </div>
          <p className="tw-help" style={{ marginTop: 10 }}>
            {`Выбрано: ${minutesLabel(draft.minutes)}. Когда время выйдет, тест завершится сам.`}
          </p>
        </div>
        <div>
          <button type="button" className="tw-btn tw-btn-quiet" aria-expanded={moreOpen} onClick={onToggleMore}>
            Дополнительно — можно пропустить
            <Icon name="down" />
          </button>
          {moreOpen && (
            <div className="tw-panel tw-stack-sm" style={{ marginTop: 12 }}>
              <div>
                <div className="tw-label">Раздел теста</div>
                <div className="tw-row" role="group" aria-label="Раздел теста">
                  {BLOCKS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className="tw-chip"
                      aria-pressed={draft.block === key}
                      onClick={() => onChange({ block: key })}
                    >
                      {BLOCK_LABEL[key]}
                    </button>
                  ))}
                </div>
                <p className="tw-help" style={{ marginTop: 8 }}>Потом изменить нельзя.</p>
              </div>
              <div>
                <div className="tw-label">Сколько процентов верных ответов нужно, чтобы сдать тест?</div>
                <p className="tw-help">{`${PASS_PERCENT}% — порог по умолчанию. После создания его можно изменить во вкладке «Настройки».`}</p>
              </div>
              <div>
                <label className="tw-label" htmlFor="tw-w-desc">Пояснение для сотрудников (необязательно)</label>
                <input
                  id="tw-w-desc"
                  className="tw-field"
                  type="text"
                  maxLength={5000}
                  placeholder="Например: по материалам октябрьского обучения"
                  value={draft.description}
                  onChange={(event) => onChange({ description: event.target.value })}
                />
              </div>
              <div>
                <label className="tw-label" htmlFor="tw-w-sub">Тема (необязательно)</label>
                <select
                  id="tw-w-sub"
                  className="tw-field"
                  value={draft.subgroup}
                  onChange={(event) => onChange({ subgroup: event.target.value })}
                >
                  <option value="">Без темы</option>
                  {subgroups.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="tw-foot">
        <button type="button" className="tw-btn tw-btn-primary tw-btn-big" disabled={!hasTitle} data-guide="wizard-name-next" onClick={onNext}>
          Дальше: вопросы
          <Icon name="right" />
        </button>
        {!hasTitle && <span className="tw-help">Впишите название теста, чтобы продолжить.</span>}
      </div>
    </>
  );
};
